import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  BriefcaseIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/solid';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const CASE_TYPE_OPTIONS = [
  'Personal Injury',
  'Corporate Law',
  'Family Law',
  'Criminal Defense',
  'Real Estate',
  'Employment Law',
  'Intellectual Property',
  'Immigration',
  'Tax Law',
  'Bankruptcy',
  'Environmental Law',
  'Other',
];

const EditCase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    client: '',
    description: '',
    case_type: '',
    customCaseType: '',
    status: 'open',
    case_number: '',
    assigned_attorneys: [],
  });

  const [clients, setClients] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [isSoleOperator, setIsSoleOperator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Quick Add Client state
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', email: '' });
  const [addingClient, setAddingClient] = useState(false);
  const [addClientError, setAddClientError] = useState('');
  const [duplicateClient, setDuplicateClient] = useState(null);

  const handleAddClient = async () => {
    if (!newClient.first_name.trim() || !newClient.last_name.trim()) {
      setAddClientError('First name and last name are required.');
      return;
    }
    setAddingClient(true);
    setAddClientError('');
    setDuplicateClient(null);
    try {
      const res = await api.post('/api/clients/', {
        first_name: newClient.first_name.trim(),
        last_name: newClient.last_name.trim(),
        email: newClient.email.trim() || undefined,
      });
      const created = res.data || res;
      setClients((prev) => [...prev, created]);
      setFormData((prev) => ({ ...prev, client: String(created.id) }));
      setNewClient({ first_name: '', last_name: '', email: '' });
      setShowAddClient(false);
    } catch (err) {
      const data = err.response?.data;
      const isEmailDuplicate = data?.email && (
        (Array.isArray(data.email) && data.email.some((m) => /already exists/i.test(m))) ||
        (typeof data.email === 'string' && /already exists/i.test(data.email))
      );

      if (isEmailDuplicate && newClient.email.trim()) {
        const existing = clients.find(
          (c) => c.email && c.email.toLowerCase() === newClient.email.trim().toLowerCase()
        );
        if (existing) {
          const nameMatches =
            existing.first_name.toLowerCase() === newClient.first_name.trim().toLowerCase() &&
            existing.last_name.toLowerCase() === newClient.last_name.trim().toLowerCase();
          if (nameMatches) {
            setFormData((prev) => ({ ...prev, client: String(existing.id) }));
            setNewClient({ first_name: '', last_name: '', email: '' });
            setShowAddClient(false);
            setAddClientError('');
          } else {
            setDuplicateClient(existing);
            setAddClientError('');
          }
        } else {
          try {
            const freshRes = await api.get('/api/clients/');
            const fd = freshRes?.data ?? freshRes;
            const freshClients = Array.isArray(fd) ? fd : Array.isArray(fd?.results) ? fd.results : [];
            setClients(freshClients);
            const found = freshClients.find(
              (c) => c.email && c.email.toLowerCase() === newClient.email.trim().toLowerCase()
            );
            if (found) {
              setFormData((prev) => ({ ...prev, client: String(found.id) }));
              setNewClient({ first_name: '', last_name: '', email: '' });
              setShowAddClient(false);
            } else {
              setAddClientError('A client with this email exists but could not be found. Please select them from the dropdown.');
            }
          } catch {
            setAddClientError('A client with this email already exists. Please select them from the dropdown.');
          }
        }
      } else if (data) {
        const msg = typeof data === 'string' ? data
          : data.email ? `Email: ${Array.isArray(data.email) ? data.email.join(' ') : data.email}`
          : data.detail || Object.values(data).flat().join(' ');
        setAddClientError(msg);
      } else {
        setAddClientError('Failed to add client. Please try again.');
      }
    } finally {
      setAddingClient(false);
    }
  };

  const handleUseExistingClient = () => {
    if (duplicateClient) {
      setFormData((prev) => ({ ...prev, client: String(duplicateClient.id) }));
      setNewClient({ first_name: '', last_name: '', email: '' });
      setDuplicateClient(null);
      setShowAddClient(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [caseRes, clientsRes, usersRes] = await Promise.all([
          api.get(`/api/cases/${id}/`),
          api.get('/api/clients/'),
          api.get('/api/accounts/users/?role=attorney&role=paralegal'),
        ]);

        const extractArr = (r) => {
          const d = r?.data ?? r;
          if (Array.isArray(d)) return d;
          if (d && Array.isArray(d.results)) return d.results;
          return [];
        };
        const caseData = caseRes?.data ?? caseRes;
        const clientsData = extractArr(clientsRes);
        const usersData = extractArr(usersRes);

        setClients(clientsData);
        setAttorneys(usersData);

        // Sole operator: no other staff, or only the current user returned
        const otherStaff = usersData.filter((u) => u.id !== currentUser?.id);
        if (otherStaff.length === 0 && currentUser?.id) {
          setIsSoleOperator(true);
        }

        // Determine if case_type is a standard option or custom
        const isStandardType = CASE_TYPE_OPTIONS.includes(caseData.case_type);

        setFormData({
          title: caseData.title || '',
          client: caseData.client ? String(caseData.client) : '',
          description: caseData.description || '',
          case_type: isStandardType ? caseData.case_type : (caseData.case_type ? 'Other' : ''),
          customCaseType: isStandardType ? '' : (caseData.case_type || ''),
          status: caseData.status || 'open',
          case_number: caseData.case_number || '',
          assigned_attorneys: caseData.assigned_attorneys
            ? caseData.assigned_attorneys.map(String)
            : [],
        });
      } catch (err) {
        console.error('Failed to load case data:', err);
        setErrors({ general: 'Failed to load case data. Please try again.' });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id, currentUser?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const toggleAttorney = (userId) => {
    setFormData((prev) => {
      const current = prev.assigned_attorneys;
      const next = current.includes(userId)
        ? current.filter((a) => a !== userId)
        : [...current, userId];
      return { ...prev, assigned_attorneys: next };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Case title is required.';
    if (!formData.client) newErrors.client = 'Please select a client.';
    if (!formData.case_type) newErrors.case_type = 'Please select a case type.';
    if (formData.case_type === 'Other' && !formData.customCaseType.trim()) {
      newErrors.customCaseType = 'Please specify the case type.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    const payload = {
      title: formData.title.trim(),
      client: parseInt(formData.client, 10),
      description: formData.description.trim(),
      case_type: formData.case_type === 'Other' ? formData.customCaseType.trim() : formData.case_type,
      status: formData.status,
      case_number: formData.case_number.trim() || null,
      assigned_attorneys: formData.assigned_attorneys.map((aid) => parseInt(aid, 10)),
    };

    try {
      await api.patch(`/api/cases/${id}/`, payload);
      setSuccessMessage('Case updated successfully! Redirecting...');
      setTimeout(() => navigate(`/cases/${id}`), 1200);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrors = {};
        Object.entries(data).forEach(([field, messages]) => {
          fieldErrors[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          setErrors({ general: 'Failed to update case. Please try again.' });
        }
      } else {
        setErrors({ general: err.message || 'Failed to update case. Please try again.' });
      }
      console.error('Case update error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading case data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/cases/${id}`)}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center mb-3 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Case Details
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <BriefcaseIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Case</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Update the case details below.</p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 flex items-center gap-3">
          <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {/* General Error */}
      {errors.general && (
        <div className="mb-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{errors.general}</p>
          </div>
          <button onClick={() => setErrors((p) => { const n = {...p}; delete n.general; return n; })}>
            <XMarkIcon className="h-4 w-4 text-red-400" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Case Details Card */}
        <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-700/10 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-gray-400" />
            Case Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Case Title */}
            <div className="md:col-span-2">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Case Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`block w-full rounded-lg border ${
                  errors.title ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                } shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5`}
              />
              {errors.title && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.title}</p>}
            </div>

            {/* Case Number */}
            <div>
              <label htmlFor="case_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Case Number
              </label>
              <input
                type="text"
                id="case_number"
                name="case_number"
                value={formData.case_number}
                onChange={handleChange}
                placeholder="e.g., HC 1234/2026"
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5"
              />
            </div>

            {/* Client Selection */}
            <div>
              <label htmlFor="client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  id="client"
                  name="client"
                  value={formData.client}
                  onChange={handleChange}
                  className={`block w-full rounded-lg border ${
                    errors.client ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5`}
                >
                  <option value="">-- Select a client --</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} {c.email ? `(${c.email})` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setShowAddClient(true); setAddClientError(''); }}
                  title="Quick add a new client"
                  className="inline-flex items-center px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors flex-shrink-0"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              {errors.client && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.client}</p>}

              {/* Quick Add Client Inline Form */}
              {showAddClient && (
                <div className="mt-3 rounded-lg border-2 border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <UserPlusIcon className="h-4 w-4 text-primary-500" />
                      Quick Add Client
                    </h4>
                    <button
                      type="button"
                      onClick={() => { setShowAddClient(false); setAddClientError(''); setDuplicateClient(null); }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {addClientError && (
                    <p className="text-xs text-red-600 dark:text-red-400 mb-2">{addClientError}</p>
                  )}

                  {/* Duplicate Client Confirmation */}
                  {duplicateClient && (
                    <div className="mb-3 rounded-md border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">
                        A client with this email already exists with different details:
                      </p>
                      <div className="flex items-center gap-2 mb-2 text-xs text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Existing:</span>
                        <span>{duplicateClient.first_name} {duplicateClient.last_name} ({duplicateClient.email})</span>
                      </div>
                      <div className="flex items-center gap-2 mb-3 text-xs text-gray-700 dark:text-gray-300">
                        <span className="font-medium">You entered:</span>
                        <span>{newClient.first_name} {newClient.last_name} ({newClient.email})</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleUseExistingClient}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                        >
                          Use Existing Client
                        </button>
                        <button
                          type="button"
                          onClick={() => setDuplicateClient(null)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Edit Details
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newClient.first_name}
                        onChange={(e) => setNewClient((p) => ({ ...p, first_name: e.target.value }))}
                        placeholder="John"
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm dark:bg-gray-700 dark:text-white px-2.5 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newClient.last_name}
                        onChange={(e) => setNewClient((p) => ({ ...p, last_name: e.target.value }))}
                        placeholder="Doe"
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm dark:bg-gray-700 dark:text-white px-2.5 py-2"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Email <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm dark:bg-gray-700 dark:text-white px-2.5 py-2"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowAddClient(false); setAddClientError(''); setDuplicateClient(null); }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddClient}
                      disabled={addingClient}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {addingClient ? (
                        <>
                          <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Adding...
                        </>
                      ) : (
                        <>
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Add Client
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Case Type */}
            <div>
              <label htmlFor="case_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Case Type <span className="text-red-500">*</span>
              </label>
              <select
                id="case_type"
                name="case_type"
                value={formData.case_type}
                onChange={handleChange}
                className={`block w-full rounded-lg border ${
                  errors.case_type ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                } shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5`}
              >
                <option value="">-- Select case type --</option>
                {CASE_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.case_type && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.case_type}</p>}
            </div>

            {/* Custom Case Type */}
            {formData.case_type === 'Other' && (
              <div className="md:col-span-2">
                <label htmlFor="customCaseType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Specify Case Type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="customCaseType"
                  name="customCaseType"
                  value={formData.customCaseType}
                  onChange={handleChange}
                  className={`block w-full rounded-lg border ${
                    errors.customCaseType ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5`}
                />
                {errors.customCaseType && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.customCaseType}</p>}
              </div>
            )}

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5"
              >
                <option value="open">Open</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the case facts, key issues, and objectives..."
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white px-3 py-2.5"
              />
            </div>
          </div>
        </div>

        {/* Assign Team Card */}
        <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-700/10 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5 text-gray-400" />
            Assign Team Members
          </h3>

          {isSoleOperator ? (
            <div className="rounded-lg border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {currentUser?.first_name} {currentUser?.last_name}
                    <span className="ml-2 text-xs text-primary-600 dark:text-primary-400 font-normal">(You)</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{currentUser?.role || 'Owner'}</p>
                </div>
                <CheckIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                You are the sole operator. This case will be automatically assigned to you.
              </p>
            </div>
          ) : attorneys.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {attorneys.map((user) => {
                const isSelected = formData.assigned_attorneys.includes(String(user.id));
                const isCurrentUser = user.id === currentUser?.id;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggleAttorney(String(user.id))}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      isSelected
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user.first_name} {user.last_name}
                        {isCurrentUser && <span className="ml-1 text-xs text-primary-500 font-normal">(You)</span>}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.role}</p>
                    </div>
                    {isSelected && <CheckIcon className="h-4 w-4 text-primary-500 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No staff members available. The case will be assigned to you automatically.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => navigate(`/cases/${id}`)}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving Changes...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCase;
